import { C } from "@bd/content";
import { priceIndex, WEEKS_PER_YEAR, yearOf } from "./calendar";
import { BALANCE } from "./balance";
import { heat } from "./market";
import type { Company, GameState } from "./state";
import { log } from "./util";

/**
 * Imprints: a rival you buy out keeps publishing under its own name, for you. Its back catalogue pays the
 * buyout royalty as before (six years); on top, its new releases bring a small weekly income for good.
 * You can point an imprint at an audience (steady) or a theme (rides the theme's heat), once a year.
 */

export interface Imprint {
  rival: string;
  /** the week you bought them */
  since: number;
  /** what their new releases earn a week before the focus, in 1965 dollars */
  base: number;
  /** "a:<audience>" or "t:<theme>"; none keeps their own mix */
  focus?: string;
  focusWeek?: number;
}

/** Start an imprint for a rival just bought (the buyout's price sets its size). */
export function foundImprint(state: GameState, co: Company, rival: string, price: number): Imprint {
  const list = (co.imprints ??= []);
  const had = list.find((x) => x.rival === rival);
  if (had) return had;
  const imp: Imprint = { rival, since: state.week, base: (price / 620 / priceIndex(state.week)) * BALANCE.imprintShare };
  list.push(imp);
  return imp;
}

/** Older saves bought rivals before imprints existed: they become imprints now, sized from what the rival was. */
export function ensureImprints(state: GameState, co: Company): void {
  for (const rv of state.rivals) {
    if (rv.acquiredBy !== co.id || co.imprints?.some((x) => x.rival === rv.id)) continue;
    const price = (rv.released * 12000 + rv.reputation * 25000) * 1.4;
    (co.imprints ??= []).push({ rival: rv.id, since: rv.acquiredWeek ?? state.week, base: (price / 620) * BALANCE.imprintShare });
  }
}

/** How much the focus lifts (or drags) what the imprint's new games earn. */
export function focusMult(state: GameState, imp: Imprint): number {
  if (!imp.focus) return 1;
  const F = BALANCE.imprintFocus;
  const [kind, id] = [imp.focus.slice(0, 1), imp.focus.slice(2)];
  const rc = C.rival[imp.rival];
  if (kind === "a") {
    const a = C.audience[id];
    if (!a || a.year > yearOf(state.week)) return F.audienceClosed;
    return rc?.audiences.includes(id as never) ? F.audienceMatch : F.audienceOther;
  }
  if (kind === "t") return F.themeBase + F.themeHeat * heat(state, "theme", id) + (rc?.themes.includes(id) ? F.themeMatch : 0);
  return 1;
}

/** What one imprint's new releases pay this week, in today's dollars. */
export function imprintWeek(state: GameState, imp: Imprint): number {
  return imp.base * priceIndex(state.week) * focusMult(state, imp);
}

/** Everything your imprints' new releases pay this week (booked with royalties by the weekly accounts). */
export function imprintsWeekly(state: GameState, co: Company): number {
  let sum = 0;
  for (const imp of co.imprints ?? []) sum += imprintWeek(state, imp);
  return sum;
}

/** The name a focus reads as: an audience's or a theme's. */
export function focusName(focus: string | undefined): string {
  if (!focus) return "";
  const id = focus.slice(2);
  return (focus.startsWith("a:") ? C.audience[id]?.name : C.theme[id]?.name) ?? id;
}

/** Every focus an imprint can take right now: open audiences, then themes that exist this year. */
export function focusOptions(state: GameState): { id: string; name: string; kind: "audience" | "theme" }[] {
  const year = yearOf(state.week);
  return [
    ...Object.values(C.audience).filter((a) => a.year <= year).map((a) => ({ id: `a:${a.id}`, name: a.name, kind: "audience" as const })),
    ...Object.values(C.theme).filter((t) => t.year <= year && !t.hidden).map((t) => ({ id: `t:${t.id}`, name: t.name, kind: "theme" as const })),
  ];
}

export interface ImprintRow {
  rival: string;
  name: string;
  since: number;
  focus?: string;
  focusName: string;
  /** the focus's lift right now (1 = none) */
  mult: number;
  /** a year of their new releases at this week's rate, today's dollars */
  yearly: number;
  /** a year of the buyout's catalogue royalty while it runs, and the week it ends */
  catalogueYearly: number;
  catalogueUntil: number | null;
  /** the week the focus can next change (now or earlier: it can) */
  refocusAt: number;
}

/** Your imprints as the Imprints screen lists them. */
export function imprintRows(state: GameState, co: Company): ImprintRow[] {
  const idx = priceIndex(state.week);
  return (co.imprints ?? []).map((imp) => {
    const name = C.rival[imp.rival]?.name ?? imp.rival;
    const cat = co.mods.find((m) => m.kind === "royalty" && m.label === `${name} catalogue` && m.until > state.week);
    return {
      rival: imp.rival, name, since: imp.since, focus: imp.focus, focusName: focusName(imp.focus), mult: focusMult(state, imp),
      yearly: Math.round(imprintWeek(state, imp) * WEEKS_PER_YEAR),
      catalogueYearly: cat ? Math.round(cat.value * idx * WEEKS_PER_YEAR) : 0, catalogueUntil: cat ? cat.until : null,
      refocusAt: imp.focusWeek === undefined ? state.week : imp.focusWeek + BALANCE.imprintRefocusWeeks,
    };
  });
}

/** The imprintFocus command: point an imprint at an audience or a theme ("" goes back to their own mix). */
export function setImprintFocus(state: GameState, co: Company, rival: string, focus: string): string | null {
  const imp = co.imprints?.find((x) => x.rival === rival);
  if (!imp) return "That publisher isn't one of your imprints.";
  if (focus && !focusOptions(state).some((o) => o.id === focus)) return "They can't make that yet.";
  if ((imp.focus ?? "") === focus) return null;
  if (imp.focusWeek !== undefined && state.week < imp.focusWeek + BALANCE.imprintRefocusWeeks) {
    const left = imp.focusWeek + BALANCE.imprintRefocusWeeks - state.week;
    return `Their next catalogue is already planned: you can change their focus in ${left} week${left === 1 ? "" : "s"}.`;
  }
  const name = C.rival[rival]?.name ?? rival;
  if (focus) imp.focus = focus;
  else delete imp.focus;
  imp.focusWeek = state.week;
  if (focus) log(state, "imprint", `Your imprint ${name} now focuses on ${focusName(focus)}.`, co.id);
  else log(state, "imprint", `Your imprint ${name} goes back to its own mix.`, co.id);
  return null;
}

export const imprintSystem = {
  name: "imprints",
  week(state: GameState): void {
    for (const co of state.companies) ensureImprints(state, co);
  },
};
